import {
  CREATE_CREATIVES,
  GET_MGID_TOKEN,
  CREATE_CREATIVES_FOR_CAMPAIGN,
  UPLOAD_FILES_TO_CDN
} from "@/constants/actions";
import {
  UPDATE_CREATIVES_FORM,
  UPDATE_CREATIVES_FORM_CAMPAIGN_BY_INDEX,
  UPDATE_CREATIVES_AUTHENTICATION,
  UPDATE_CREATIVES_PROGRESS_MODAL,
  UPDATE_IS_LOADING,
  EMPTY_STATE
} from "@/constants/mutations";
import { CreativesService } from "@/services/createCreatives/CreativesService";
import {
  getCreativeFiles,
  getCreativeTexts,
  getCreativeUrl
} from "@/helpers/createCreatives/files";

const initialState = () => {
  return {
    form: {
      email: null,
      password: null,
      clientId: null,
      categoryId: 100,
      priceOfClick: "3.0",
      campaigns: [
        { id: null },
        { id: null },
        { id: null },
        { id: null },
        { id: null }
      ]
    },
    authentication: {
      token: null
    },
    progressModal: {
      isActive: false,
      done: null,
      total: null,
      statusMessage: null,
      errorMessage: null
    },
    isLoading: false
  };
};

const state = initialState();

const getters = {};

const mutations = {
  [UPDATE_CREATIVES_FORM] (state, form) {
    state.form = { ...state.form, ...form };
  },

  [UPDATE_CREATIVES_FORM_CAMPAIGN_BY_INDEX] (state, { index, campaign }) {
    const targetCampaign = state.form.campaigns?.[index];

    if (targetCampaign) {
      Object.assign(state.form.campaigns[index], campaign);
    }
  },

  [UPDATE_CREATIVES_AUTHENTICATION] (state, authentication) {
    state.authentication = { ...state.authentication, ...authentication };
  },

  [UPDATE_CREATIVES_PROGRESS_MODAL] (state, progressModal) {
    state.progressModal = { ...state.progressModal, ...progressModal };
  },

  [UPDATE_IS_LOADING] (state, isLoading) {
    state.isLoading = isLoading;
  },

  [EMPTY_STATE] (state) {
    Object.assign(state, initialState());
  }
};

const actions = {
  async [CREATE_CREATIVES] ({ state, commit, dispatch }, directoryFiles) {
    try {
      commit(UPDATE_IS_LOADING, true);

      const creativeFiles = getCreativeFiles(directoryFiles);
      const creativeTexts = await getCreativeTexts(directoryFiles);
      const creativeUrl = await getCreativeUrl(directoryFiles);

      const campaignIds = state.form.campaigns
        .map((campaign) => {
          return campaign.id;
        })
        .filter((campaignId) => campaignId !== null);

      const campaignPayloads = campaignIds.map((campaignId, index) => {
        const startIndex = index * 10;
        const endIndex = 10 * (index + 1);
        const campaignFileList = creativeFiles.slice(startIndex, endIndex);
        const campaignTexts = {};

        campaignFileList.forEach((file) => {
          const textKey = file.name.split(".")[0];

          campaignTexts[textKey] = creativeTexts[textKey];
        });

        return {
          campaignId,
          campaignFileList,
          campaignTexts,
          creativeUrl
        };
      });

      commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
        isActive: true,
        total: campaignIds.length,
        done: 0,
        statusMessage: "Authentication in MGID account...",
        errorMessage: null
      });

      try {
        await dispatch(GET_MGID_TOKEN);
      } catch (error) {
        commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
          statusMessage: null,
          errorMessage: `Failed to authenticate in MGID account.\n${ error }`
        });

        throw error;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const payload of campaignPayloads) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(CREATE_CREATIVES_FOR_CAMPAIGN, payload);

        commit(UPDATE_CREATIVES_PROGRESS_MODAL, { done: state.progressModal.done + 1 });
      }
    } catch (error) {
      throw error;
    } finally {
      commit(UPDATE_IS_LOADING, false);
    }
  },

  async [GET_MGID_TOKEN] ({ commit, state: { form } }) {
    const {
      email,
      password
    } = form;

    const { data: { token } } = await CreativesService.getMgidAuthToken(email, password);

    commit(UPDATE_CREATIVES_AUTHENTICATION, { token });
  },

  async [CREATE_CREATIVES_FOR_CAMPAIGN] ({ state, commit, dispatch }, payload) {
    const {
      token
    } = state.authentication;
    const {
      clientId,
      categoryId,
      priceOfClick
    } = state.form;
    const {
      campaignId,
      campaignFileList,
      campaignTexts,
      creativeUrl
    } = payload;

    let campaignFiles = null;

    try {
      commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
        statusMessage: `Campaign: ${ payload.campaignId }. Uploading files to CDN...`,
        errorMessage: null
      });

      campaignFiles = await dispatch(UPLOAD_FILES_TO_CDN, campaignFileList);
    } catch (error) {
      commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
        statusMessage: null,
        errorMessage: `Campaign: ${ payload.campaignId }. Failed to upload files to CDN\n${ error }`
      });

      throw error;
    }

    const creativeDataArray = [];
    const campaignFileKeys = Object.keys(campaignFiles);

    campaignFileKeys.forEach((fileKey) => {
      const creativeData = {
        url: creativeUrl,
        campaignId,
        title: campaignTexts[fileKey],
        imageLink: campaignFiles[fileKey],
        category: categoryId,
        priceOfClick: priceOfClick ? Number(priceOfClick) : null,
        whetherShowGoodPrice: 0
      };

      creativeDataArray.push(creativeData);
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const data of creativeDataArray) {
      try {
        commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
          statusMessage: `Campaign: ${ payload.campaignId }. Creating creatives...`,
          errorMessage: null
        });

        // eslint-disable-next-line no-await-in-loop
        const { data: { errors } } = await CreativesService.createCampaignCreative(token, clientId, data);

        if (Array.isArray(errors) && errors.length !== 0) {
          throw new Error(errors[0]);
        }
      } catch (error) {
        commit(UPDATE_CREATIVES_PROGRESS_MODAL, {
          statusMessage: null,
          errorMessage: `Campaign: ${ payload.campaignId }. Failed to create creatives\n${ error }`
        });

        throw error;
      }
    }
  },

  async [UPLOAD_FILES_TO_CDN] (_, fileList) {
    const filesObject = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const file of fileList) {
      // eslint-disable-next-line no-await-in-loop
      const { data: { filename, link } } = await CreativesService.uploadFileToCdn(file);
      const key = filename.split(".")[0];

      filesObject[key] = link;
    }

    return filesObject;
  }
};

export const createCreatives = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
